import { categorieArticle } from '@/views/service-stock/stock-categories'

// Réactifs suivis au flacon (04/10/2026) : un réactif ne se consomme pas à l'unité. On ouvre un flacon (la réserve
// baisse de 1), on l'utilise, puis on le termine. Fonctions pures partagées par l'écran et ses fenêtres.

export const MOTIFS_FIN = [
    { id: 'epuise', libelle: 'Épuisé', aide: 'terminé normalement' },
    { id: 'perime', libelle: 'Périmé', aide: 'date limite atteinte' },
    { id: 'jete', libelle: 'Jeté', aide: 'cassé, contaminé…' },
    { id: 'cq_non_conforme', libelle: 'Contrôle qualité non conforme', aide: '' }
]

export const JOURS_ALERTE = 3

const deux = n => (n < 10 ? '0' : '') + n

/** Date du jour du poste au format AAAA-MM-JJ. */
export function aujourdhuiLocal(maintenant) {
    const d = maintenant || new Date()
    return d.getFullYear() + '-' + deux(d.getMonth() + 1) + '-' + deux(d.getDate())
}

/** Fuseau du navigateur, envoyé au serveur pour qu'« aujourd'hui » soit la date locale. */
export function fuseauNavigateur() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch (e) { return '' }
}

/** Clé unique d'une ouverture : un double clic n'ouvre qu'un flacon. */
export function cleRequete() {
    return 'flacon-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10)
}

/** « 2026-11-01 » → « 01/11/2026 » ; date et heure ISO → date locale. */
export function dateCourte(valeur) {
    if (!valeur) return ''
    const texte = String(valeur)
    if (/^\d{4}-\d{2}-\d{2}$/.test(texte)) return texte.slice(8, 10) + '/' + texte.slice(5, 7) + '/' + texte.slice(0, 4)
    const date = new Date(texte)
    return isNaN(date.getTime()) ? '' : deux(date.getDate()) + '/' + deux(date.getMonth() + 1) + '/' + date.getFullYear()
}

function ecartJours(de, a) {
    return Math.round((Date.UTC(+a.slice(0, 4), +a.slice(5, 7) - 1, +a.slice(8, 10)) - Date.UTC(+de.slice(0, 4), +de.slice(5, 7) - 1, +de.slice(8, 10))) / 86400000)
}

/** Ajoute des jours à une date AAAA-MM-JJ. */
export function ajouterJours(date, jours) {
    const d = new Date(Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10) + Number(jours)))
    return d.getUTCFullYear() + '-' + deux(d.getUTCMonth() + 1) + '-' + deux(d.getUTCDate())
}

/** Comme le serveur : la plus proche de la péremption et de l'ouverture + stabilité. */
export function limiteUtilisation(aujourdhui, peremption, stabilite) {
    const parStabilite = Number(stabilite) > 0 ? ajouterJours(aujourdhui, Number(stabilite)) : ''
    if (peremption && (!parStabilite || peremption < parStabilite)) return peremption
    return parStabilite
}

/** Mode de suivi : réglage de l'article, sinon au flacon pour les réactifs et à l'unité pour le reste. */
export function suiviArticle(article, reglage) {
    if (reglage && (reglage.suivi === 'flacon' || reglage.suivi === 'unite')) return reglage.suivi
    return categorieArticle(article || {}) === 'reactifs' ? 'flacon' : 'unite'
}

/** Niveau d'un flacon ouvert : dépassé (rouge), à J-3 ou moins (orange), sinon normal. */
export function niveauFlacon(flacon, aujourdhui) {
    if (!flacon || !flacon.a_utiliser_avant) return 'ok'
    const reste = ecartJours(aujourdhui, flacon.a_utiliser_avant)
    if (reste < 0) return 'depasse'
    if (reste <= JOURS_ALERTE) return 'proche'
    return 'ok'
}

/** « lot G2309 · ouvert le 02/10/2026 par Alice MBEMBA · à utiliser avant le 01/11/2026 » */
export function resumeFlacon(flacon, aujourdhui) {
    const morceaux = []
    if (flacon.lot) morceaux.push('lot ' + flacon.lot)
    morceaux.push('ouvert le ' + dateCourte(flacon.ouvert_le) + (flacon.ouvert_par_nom ? ' par ' + flacon.ouvert_par_nom : ''))
    if (flacon.a_utiliser_avant) {
        const niveau = niveauFlacon(flacon, aujourdhui)
        morceaux.push((niveau === 'depasse' ? 'à remplacer : limite dépassée le ' : 'à utiliser avant le ') + dateCourte(flacon.a_utiliser_avant))
    }
    return morceaux.join(' · ')
}

/** Couverture de la réserve d'après la durée moyenne des flacons épuisés. */
export function prevision(reserve, reglage) {
    const duree = reglage && Number(reglage.duree_moyenne_jours) > 0 ? Number(reglage.duree_moyenne_jours) : 0
    if (!duree) return 'Durée moyenne connue après le premier flacon épuisé.'
    const moyenne = 'Un flacon dure en moyenne ' + String(Math.round(duree * 10) / 10).replace('.', ',') + ' j'
    if (!(reserve > 0)) return moyenne + ' — plus aucun flacon en réserve.'
    const jours = Math.round(reserve * duree)
    const couverture = jours < 14 ? jours + ' jour' + (jours > 1 ? 's' : '') : Math.round(jours / 7) + ' semaines'
    return moyenne + ' → réserve ≈ ' + couverture + '.'
}

/** Ajoute à une ligne du stock son mode de suivi, ses flacons en cours et la prévision. */
export function enrichirLigne(ligne, donnees, aujourdhui) {
    const reglages = (donnees && donnees.articles) || []
    const reglage = reglages.find(r => Number(r.article_id) === Number(ligne.id)) || null
    const suivi = donnees ? suiviArticle(ligne, reglage) : 'unite'
    const en_cours = ((donnees && donnees.en_cours) || []).filter(f => Number(f.article_id) === Number(ligne.id))
        .map(f => Object.assign({}, f, { niveau: niveauFlacon(f, aujourdhui), resume: resumeFlacon(f, aujourdhui) }))
    const precedents = ((donnees && donnees.en_cours) || []).concat((donnees && donnees.termines) || [])
        .filter(f => Number(f.article_id) === Number(ligne.id))
        .sort((a, b) => String(b.ouvert_le || '').localeCompare(String(a.ouvert_le || '')))
    return Object.assign({}, ligne, {
        suivi,
        suivi_regle: reglage ? reglage.suivi : null,
        stabilite_jours: reglage ? reglage.stabilite_jours : null,
        en_cours,
        dernier_flacon: precedents[0] || null,
        prevision: suivi === 'flacon' ? prevision(ligne.quantite, reglage) : ''
    })
}
